import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { TEAM_STATS_TEAMS, getStatsCollectionRoot } from '../utils/teamStatsConfig';
import './PracticeLineBoard.css';

const DROPPABLE_BENCH_F = 'bench-forwards';
const DROPPABLE_BENCH_D = 'bench-defense';
const DROPPABLE_GOALIES = 'goalies';

const LINE_COLOR_OPTIONS = [
  { key: 'red', label: 'Red' },
  { key: 'blue', label: 'Blue' },
  { key: 'green', label: 'Green' },
  { key: 'yellow', label: 'Yellow' },
  { key: 'orange', label: 'Orange' },
  { key: 'purple', label: 'Purple' },
  { key: 'white', label: 'White' },
  { key: 'black', label: 'Black' },
];

/** Standalone document styles for iframe print/PDF (no site chrome, no admin nav). */
const PRACTICE_LINES_IFRAME_PRINT_CSS = `
@page { size: letter; margin: 10mm; }
html, body {
  margin: 0;
  padding: 0;
  background: #fff;
  color: #111;
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 11pt;
  line-height: 1.35;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.plb-print-sheet {
  display: block !important;
  box-sizing: border-box;
  max-width: 100%;
  padding: 0;
  margin: 0;
}
.plb-print-sheet-head {
  text-align: center;
  margin-bottom: 14pt;
  padding-bottom: 10pt;
  border-bottom: 2px solid #111;
}
.plb-print-sheet-title {
  margin: 0;
  font-size: 18pt;
  font-weight: 800;
  letter-spacing: -0.02em;
}
.plb-print-sheet-meta {
  margin: 6pt 0 0;
  font-size: 10pt;
  color: #333;
}
.plb-print-lines {
  display: flex;
  flex-direction: column;
  gap: 10pt;
}
.plb-print-line {
  break-inside: avoid;
  page-break-inside: avoid;
  border: 1px solid #bbb;
  border-left: 5px solid #888;
  border-radius: 3px;
  padding: 8pt 10pt 9pt;
  background: #f7f7f7;
}
.plb-print-line--red { border-left-color: #b71c1c; }
.plb-print-line--blue { border-left-color: #0d47a1; }
.plb-print-line--green { border-left-color: #1b5e20; }
.plb-print-line--yellow { border-left-color: #f57f17; }
.plb-print-line--orange { border-left-color: #e65100; }
.plb-print-line--purple { border-left-color: #4a148c; }
.plb-print-line--white { border-left-color: #9e9e9e; }
.plb-print-line--black { border-left-color: #263238; }
.plb-print-line-heading {
  margin: 0 0 6pt;
  font-size: 11pt;
  font-weight: 700;
}
.plb-print-line-pinney { font-weight: 600; color: #333; }
.plb-print-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10pt;
}
.plb-print-table th {
  text-align: left;
  vertical-align: top;
  width: 4.2rem;
  padding: 2pt 8pt 2pt 0;
  font-weight: 700;
  color: #222;
  border: none;
}
.plb-print-table td {
  padding: 2pt 0;
  border: none;
  line-height: 1.4;
}
.plb-print-cell {
  display: inline-block;
  margin-right: 14pt;
  margin-bottom: 2pt;
}
.plb-print-cell abbr {
  font-weight: 700;
  color: #000;
  text-decoration: none;
  margin-right: 3pt;
}
.plb-print-footer {
  margin-top: 14pt;
  padding-top: 10pt;
  border-top: 1px solid #999;
  break-inside: avoid;
}
.plb-print-footer-title {
  margin: 8pt 0 3pt;
  font-size: 8.5pt;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #222;
}
.plb-print-footer-title:first-child { margin-top: 0; }
.plb-print-footer-body {
  margin: 0;
  font-size: 10pt;
  line-height: 1.4;
  color: #111;
}
`;

function escapeHtmlText(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function slotId(lineId, kind, index) {
  return `line:${lineId}:${kind}:${index}`;
}

function parseSlotId(id) {
  if (id === DROPPABLE_BENCH_F || id === DROPPABLE_BENCH_D || id === DROPPABLE_GOALIES) return { type: id };
  const m = String(id).match(/^line:([^:]+):([fd]):(\d)$/);
  if (!m) return null;
  return { type: 'slot', lineId: m[1], kind: m[2], index: parseInt(m[3], 10) };
}

/** Stable ids so Firestore reconcile does not invent new lines every snapshot. */
function defaultLinesStable() {
  return LINE_COLOR_OPTIONS.slice(0, 4).map((c, i) => ({
    id: `default-line-${i}`,
    label: `Line ${i + 1}`,
    color: c.key,
    forwards: ['', '', ''],
    defense: ['', ''],
  }));
}

function findPlayerLocation(board, name) {
  if (board.benchForwards.includes(name)) return { type: DROPPABLE_BENCH_F };
  if (board.benchDefense.includes(name)) return { type: DROPPABLE_BENCH_D };
  if (board.goalies.includes(name)) return { type: DROPPABLE_GOALIES };
  for (const line of board.lines) {
    for (let i = 0; i < 3; i++) {
      if (line.forwards[i] === name) return { type: 'slot', lineId: line.id, kind: 'f', index: i };
    }
    for (let i = 0; i < 2; i++) {
      if (line.defense[i] === name) return { type: 'slot', lineId: line.id, kind: 'd', index: i };
    }
  }
  return null;
}

function droppableIdForLocation(loc) {
  if (!loc) return null;
  if (loc.type === DROPPABLE_BENCH_F || loc.type === DROPPABLE_BENCH_D || loc.type === DROPPABLE_GOALIES) return loc.type;
  if (loc.type === 'slot') return slotId(loc.lineId, loc.kind, loc.index);
  return null;
}

/** Display-only sort; does not mutate board order in Firestore. */
function sortNamesForDisplay(names) {
  return [...names].sort((a, b) => {
    const la = String(a).trim().split(/\s+/).pop() || a;
    const lb = String(b).trim().split(/\s+/).pop() || b;
    return la.localeCompare(lb, undefined, { sensitivity: 'base' });
  });
}

function lineColorLabel(colorKey) {
  return LINE_COLOR_OPTIONS.find((c) => c.key === colorKey)?.label || colorKey;
}

function formatPlayerSlot(name, rosterByName) {
  if (!name) return '—';
  const p = rosterByName.get(name);
  const num = p?.number != null && String(p.number).trim() !== '' ? `#${p.number} ` : '';
  return `${num}${name}`;
}

/**
 * Default bench pool for a skater from Stat Tracker roster position (coaches can drag across pools anytime).
 */
function rosterSkaterBenchGroup(player) {
  if (!player || player.isGoalie) return 'f';
  const pos = String(player.position || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
  if (!pos) return 'f';
  if (pos === 'D' || pos === 'LD' || pos === 'RD' || pos === 'DEF' || pos === 'DEFENSE') return 'd';
  return 'f';
}

function pushBumpedSkatersToBenches(draft, bumpedNames, rosterByName) {
  const toF = [];
  const toD = [];
  bumpedNames.forEach((n) => {
    if (!n) return;
    (rosterSkaterBenchGroup(rosterByName.get(n)) === 'd' ? toD : toF).push(n);
  });
  draft.benchForwards = [...toF, ...draft.benchForwards];
  draft.benchDefense = [...toD, ...draft.benchDefense];
}

/** Build board from Firestore doc + current roster (names as stable ids). */
function reconcileBoardFromSources(roster, serverDoc) {
  const skaters = roster.filter((p) => !p.isGoalie);
  const goalies = roster.filter((p) => p.isGoalie);
  const skaterNames = skaters.map((p) => p.name);
  const goalieNames = goalies.map((p) => p.name);
  const valid = new Set([...skaterNames, ...goalieNames]);
  const rosterPlayerByName = (name) => skaters.find((p) => p.name === name) || goalies.find((p) => p.name === name);

  let lines;
  if (serverDoc && Array.isArray(serverDoc.lines) && serverDoc.lines.length > 0) {
    lines = serverDoc.lines.map((line, i) => ({
      id: line.id || `line-${i}`,
      label: (line.label || `Line ${i + 1}`).slice(0, 80),
      color: LINE_COLOR_OPTIONS.some((c) => c.key === line.color) ? line.color : LINE_COLOR_OPTIONS[i % LINE_COLOR_OPTIONS.length].key,
      forwards: [0, 1, 2].map((j) => {
        const n = line.forwards?.[j];
        return n && valid.has(n) ? n : '';
      }),
      defense: [0, 1].map((j) => {
        const n = line.defense?.[j];
        return n && valid.has(n) ? n : '';
      }),
    }));
  } else {
    lines = defaultLinesStable();
  }

  let benchForwards = [];
  let benchDefense = [];
  const hasSplitPools =
    Array.isArray(serverDoc?.benchForwards) || Array.isArray(serverDoc?.benchDefense);

  if (hasSplitPools) {
    benchForwards = Array.isArray(serverDoc.benchForwards) ? serverDoc.benchForwards.filter((n) => n && valid.has(n)) : [];
    benchDefense = Array.isArray(serverDoc.benchDefense) ? serverDoc.benchDefense.filter((n) => n && valid.has(n)) : [];
  } else if (Array.isArray(serverDoc?.bench)) {
    serverDoc.bench.forEach((n) => {
      if (!n || !valid.has(n)) return;
      const p = rosterPlayerByName(n);
      if (rosterSkaterBenchGroup(p) === 'd') benchDefense.push(n);
      else benchForwards.push(n);
    });
  }

  let gList = Array.isArray(serverDoc?.goalies) ? serverDoc.goalies.filter((n) => n && valid.has(n)) : [];

  const placed = new Set();
  lines.forEach((l) => {
    l.forwards.forEach((n) => {
      if (n) placed.add(n);
    });
    l.defense.forEach((n) => {
      if (n) placed.add(n);
    });
  });
  benchForwards.forEach((n) => placed.add(n));
  benchDefense.forEach((n) => placed.add(n));
  gList.forEach((n) => placed.add(n));

  skaterNames.forEach((n) => {
    if (!placed.has(n)) {
      if (rosterSkaterBenchGroup(rosterPlayerByName(n)) === 'd') benchDefense.push(n);
      else benchForwards.push(n);
    }
  });
  goalieNames.forEach((n) => {
    if (!placed.has(n)) gList.push(n);
  });

  const dedupeKeepOrder = (arr) => {
    const seen = new Set();
    return arr.filter((n) => {
      if (!n || seen.has(n)) return false;
      seen.add(n);
      return true;
    });
  };

  return {
    version: 1,
    lines,
    benchForwards: dedupeKeepOrder(benchForwards),
    benchDefense: dedupeKeepOrder(benchDefense),
    goalies: dedupeKeepOrder(gList),
  };
}

function cloneBoard(b) {
  return {
    version: b.version,
    lines: b.lines.map((l) => ({
      ...l,
      forwards: [...l.forwards],
      defense: [...l.defense],
    })),
    benchForwards: [...b.benchForwards],
    benchDefense: [...b.benchDefense],
    goalies: [...b.goalies],
  };
}

function getOccupant(board, target) {
  if (target.type === DROPPABLE_BENCH_F || target.type === DROPPABLE_BENCH_D || target.type === DROPPABLE_GOALIES) return null;
  if (target.type !== 'slot') return null;
  const line = board.lines.find((l) => l.id === target.lineId);
  if (!line) return null;
  if (target.kind === 'f') return line.forwards[target.index] || null;
  return line.defense[target.index] || null;
}

function removePlayerEverywhere(draft, name) {
  draft.benchForwards = draft.benchForwards.filter((n) => n !== name);
  draft.benchDefense = draft.benchDefense.filter((n) => n !== name);
  draft.goalies = draft.goalies.filter((n) => n !== name);
  draft.lines.forEach((line) => {
    line.forwards = line.forwards.map((n) => (n === name ? '' : n));
    line.defense = line.defense.map((n) => (n === name ? '' : n));
  });
}

function setSlotOccupant(draft, target, name) {
  const line = draft.lines.find((l) => l.id === target.lineId);
  if (!line) return;
  if (target.kind === 'f') line.forwards[target.index] = name;
  else line.defense[target.index] = name;
}

function applyDrop(board, playerName, overId, rosterByName) {
  const target = parseSlotId(overId);
  if (!target) return board;

  const draft = cloneBoard(board);
  const occupant = target.type === 'slot' ? getOccupant(board, target) : null;

  removePlayerEverywhere(draft, playerName);

  const bumpToBench = (n) => {
    if (rosterSkaterBenchGroup(rosterByName?.get(n)) === 'd') draft.benchDefense.push(n);
    else draft.benchForwards.push(n);
  };

  if (target.type === DROPPABLE_BENCH_F) {
    if (occupant && occupant !== playerName) {
      removePlayerEverywhere(draft, occupant);
      bumpToBench(occupant);
    }
    draft.benchForwards.push(playerName);
    return draft;
  }

  if (target.type === DROPPABLE_BENCH_D) {
    if (occupant && occupant !== playerName) {
      removePlayerEverywhere(draft, occupant);
      bumpToBench(occupant);
    }
    draft.benchDefense.push(playerName);
    return draft;
  }

  if (target.type === DROPPABLE_GOALIES) {
    if (occupant && occupant !== playerName) {
      removePlayerEverywhere(draft, occupant);
      draft.goalies.push(occupant);
    }
    draft.goalies.push(playerName);
    return draft;
  }

  if (target.type === 'slot') {
    if (occupant && occupant !== playerName) {
      removePlayerEverywhere(draft, occupant);
      if (rosterSkaterBenchGroup(rosterByName?.get(occupant)) === 'd') draft.benchDefense.unshift(occupant);
      else draft.benchForwards.unshift(occupant);
    }
    setSlotOccupant(draft, target, playerName);
  }

  return draft;
}

function DraggablePlayerCard({ name, number }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: name,
  });
  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 20 : undefined,
        opacity: isDragging ? 0.35 : 1,
      }
    : { opacity: isDragging ? 0.35 : 1 };

  return (
    <div
      ref={setNodeRef}
      className="plb-player-card"
      style={style}
      {...listeners}
      {...attributes}
    >
      <span className="plb-player-num">{number != null ? `#${number}` : ''}</span>
      <span className="plb-player-name">{name}</span>
    </div>
  );
}

function SlotCell({ lineId, kind, index, occupantName, rosterByName }) {
  const id = slotId(lineId, kind, index);
  const { setNodeRef, isOver } = useDroppable({ id });
  const p = occupantName ? rosterByName.get(occupantName) : null;
  const num = p?.number;

  return (
    <div ref={setNodeRef} className={`plb-slot ${isOver ? 'plb-slot--over' : ''} ${occupantName ? 'plb-slot--filled' : ''}`}>
      {occupantName ? (
        <DraggablePlayerCard name={occupantName} number={num} />
      ) : (
        <span className="plb-slot-placeholder">Drop</span>
      )}
    </div>
  );
}

function SkaterBenchPool({ droppableId, displayNames, rosterByName, sectionLabel, title, hint, emptyHint }) {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });
  return (
    <div ref={setNodeRef} className={`plb-pool plb-pool--bench plb-pool--scrollable ${isOver ? 'plb-pool--over' : ''}`}>
      <p className="plb-roster-section-label">{sectionLabel}</p>
      <h3 className="plb-pool-title">{title}</h3>
      <p className="plb-pool-hint">{hint}</p>
      <div className="plb-pool-cards plb-pool-cards--scroll">
        {displayNames.map((name) => {
          const p = rosterByName.get(name);
          return <DraggablePlayerCard key={name} name={name} number={p?.number} />;
        })}
      </div>
      {displayNames.length === 0 ? <p className="plb-empty">{emptyHint}</p> : null}
    </div>
  );
}

function GoaliePool({ displayNames, rosterByName }) {
  const { setNodeRef, isOver } = useDroppable({ id: DROPPABLE_GOALIES });
  return (
    <div ref={setNodeRef} className={`plb-pool plb-pool--goalies plb-pool--goalies-pane ${isOver ? 'plb-pool--over' : ''}`}>
      <p className="plb-roster-section-label">Goalies</p>
      <h3 className="plb-pool-title">Goalie pool</h3>
      <p className="plb-pool-hint">Drag into slots only if you use a skater spot for a goalie.</p>
      <div className="plb-pool-cards plb-pool-cards--goalies-scroll">
        {displayNames.map((name) => {
          const p = rosterByName.get(name);
          return <DraggablePlayerCard key={name} name={name} number={p?.number} />;
        })}
      </div>
      {displayNames.length === 0 ? <p className="plb-empty">None in pool — on a line or not on roster.</p> : null}
    </div>
  );
}

export default function PracticeLineBoard() {
  const [teamId, setTeamId] = useState(TEAM_STATS_TEAMS[0]?.teamId || '');
  const [roster, setRoster] = useState([]);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [serverDoc, setServerDoc] = useState(null);
  const [serverLoaded, setServerLoaded] = useState(false);
  const [board, setBoard] = useState(() => ({
    version: 1,
    lines: defaultLinesStable(),
    benchForwards: [],
    benchDefense: [],
    goalies: [],
  }));
  const [activeDragName, setActiveDragName] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const [importError, setImportError] = useState('');
  const [rosterDrawerOpen, setRosterDrawerOpen] = useState(false);
  const [benchSearch, setBenchSearch] = useState('');
  const [lineToolsExpanded, setLineToolsExpanded] = useState(true);
  const fileInputRef = useRef(null);
  const printSheetRef = useRef(null);
  const saveTimerRef = useRef(null);
  const lastSavedJson = useRef('');

  useEffect(() => {
    lastSavedJson.current = '';
  }, [teamId]);

  const rosterByName = useMemo(() => {
    const m = new Map();
    roster.forEach((p) => {
      if (p?.name) m.set(p.name, p);
    });
    return m;
  }, [roster]);

  const teamRosterRef = useMemo(() => {
    if (!teamId) return null;
    return doc(db, getStatsCollectionRoot(), teamId, 'config', 'teamRoster');
  }, [teamId]);

  const practiceLinesRef = useMemo(() => {
    if (!teamId) return null;
    return doc(db, getStatsCollectionRoot(), teamId, 'config', 'practiceLines');
  }, [teamId]);

  useEffect(() => {
    setRosterLoaded(false);
    setServerLoaded(false);
    if (!teamRosterRef) return undefined;
    const unsub = onSnapshot(
      teamRosterRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setRoster(Array.isArray(data.roster) ? data.roster : []);
        } else {
          setRoster([]);
        }
        setRosterLoaded(true);
      },
      (err) => {
        console.error('PracticeLineBoard teamRoster', err);
        setRoster([]);
        setRosterLoaded(true);
      }
    );
    return () => unsub();
  }, [teamRosterRef]);

  useEffect(() => {
    if (!practiceLinesRef) return undefined;
    const unsub = onSnapshot(
      practiceLinesRef,
      (snap) => {
        setServerDoc(snap.exists() ? snap.data() : null);
        setServerLoaded(true);
      },
      (err) => {
        console.error('PracticeLineBoard practiceLines', err);
        setServerDoc(null);
        setServerLoaded(true);
      }
    );
    return () => unsub();
  }, [practiceLinesRef]);

  useEffect(() => {
    if (!rosterLoaded || !serverLoaded) return;
    setBoard(reconcileBoardFromSources(roster, serverDoc));
  }, [roster, rosterLoaded, serverDoc, serverLoaded]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  const handleDragStart = (event) => {
    const id = event.active?.id;
    if (typeof id === 'string') setActiveDragName(id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragName(null);
    if (!over?.id || typeof active.id !== 'string') return;
    const playerName = active.id;
    let overId = String(over.id);
    if (!parseSlotId(overId)) {
      setBoard((prev) => {
        const loc = findPlayerLocation(prev, overId);
        const resolved = droppableIdForLocation(loc);
        if (!resolved) return prev;
        return applyDrop(prev, playerName, resolved, rosterByName);
      });
      return;
    }
    setBoard((prev) => applyDrop(prev, playerName, overId, rosterByName));
  };

  const handleDragCancel = () => setActiveDragName(null);

  const boardForFirestore = useMemo(() => {
    const { version, lines, benchForwards, benchDefense, goalies } = board;
    return { version, lines, benchForwards, benchDefense, goalies };
  }, [board]);

  useEffect(() => {
    if (!practiceLinesRef || !rosterLoaded || !serverLoaded) return undefined;
    const json = JSON.stringify(boardForFirestore);
    if (json === lastSavedJson.current) return undefined;
    setSaveStatus('Saving…');
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await setDoc(
          practiceLinesRef,
          {
            ...boardForFirestore,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
        lastSavedJson.current = json;
        setSaveStatus('Saved');
        setTimeout(() => setSaveStatus(''), 2000);
      } catch (e) {
        console.error('PracticeLineBoard save', e);
        setSaveStatus('Save failed');
      }
    }, 450);
    return () => clearTimeout(saveTimerRef.current);
  }, [boardForFirestore, practiceLinesRef, rosterLoaded, serverLoaded]);

  const addLine = () => {
    setBoard((prev) => {
      const i = prev.lines.length;
      const c = LINE_COLOR_OPTIONS[i % LINE_COLOR_OPTIONS.length];
      const newLine = {
        id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `line-${Date.now()}`,
        label: `Line ${i + 1}`,
        color: c.key,
        forwards: ['', '', ''],
        defense: ['', ''],
      };
      return { ...prev, lines: [...prev.lines, newLine] };
    });
  };

  const duplicateLine = (lineId) => {
    setBoard((prev) => {
      const idx = prev.lines.findIndex((l) => l.id === lineId);
      if (idx < 0) return prev;
      const src = prev.lines[idx];
      const copy = {
        id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `line-${Date.now()}`,
        label: `${src.label} (copy)`,
        color: src.color,
        forwards: [...src.forwards],
        defense: [...src.defense],
      };
      const lines = [...prev.lines.slice(0, idx + 1), copy, ...prev.lines.slice(idx + 1)];
      return { ...prev, lines };
    });
  };

  const clearLine = (lineId) => {
    setBoard((prev) => {
      const draft = cloneBoard(prev);
      const line = draft.lines.find((l) => l.id === lineId);
      if (!line) return prev;
      const bumped = [];
      line.forwards.forEach((n) => {
        if (n) bumped.push(n);
      });
      line.defense.forEach((n) => {
        if (n) bumped.push(n);
      });
      line.forwards = ['', '', ''];
      line.defense = ['', ''];
      pushBumpedSkatersToBenches(draft, bumped, rosterByName);
      return draft;
    });
  };

  const removeLine = (lineId) => {
    setBoard((prev) => {
      const line = prev.lines.find((l) => l.id === lineId);
      if (!line || prev.lines.length <= 1) return prev;
      const bumped = [];
      line.forwards.forEach((n) => {
        if (n) bumped.push(n);
      });
      line.defense.forEach((n) => {
        if (n) bumped.push(n);
      });
      const draft = {
        ...prev,
        lines: prev.lines.filter((l) => l.id !== lineId),
        benchForwards: [...prev.benchForwards],
        benchDefense: [...prev.benchDefense],
        goalies: [...prev.goalies],
      };
      pushBumpedSkatersToBenches(draft, bumped, rosterByName);
      return draft;
    });
  };

  const resetBoard = () => {
    if (!window.confirm('Reset lines to default (from roster and saved layout)? Unsaved local changes are replaced on next sync.')) return;
    setBoard(reconcileBoardFromSources(roster, serverDoc));
  };

  const fullResetFromRoster = () => {
    if (!window.confirm('Clear saved layout and rebuild from roster only?')) return;
    setBoard(reconcileBoardFromSources(roster, null));
  };

  const exportJson = useCallback(() => {
    const payload = {
      exportedAt: new Date().toISOString(),
      teamId,
      ...boardForFirestore,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `practice-lines-${teamId}-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [boardForFirestore, teamId]);

  const onImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setImportError('');
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        const data = JSON.parse(text);
        if (!data || typeof data !== 'object') throw new Error('Invalid file');
        const doc = {
          version: data.version,
          lines: data.lines,
          benchForwards: data.benchForwards,
          benchDefense: data.benchDefense,
          bench: data.bench,
          goalies: data.goalies,
        };
        setBoard(reconcileBoardFromSources(roster, doc));
      } catch (err) {
        setImportError(err?.message || 'Could not read file');
      }
    };
    reader.readAsText(file);
  };

  const collisionDetection = useCallback((args) => {
    const pointer = pointerWithin(args);
    if (pointer.length) return pointer;
    return rectIntersection(args);
  }, []);

  const activePlayer = activeDragName ? rosterByName.get(activeDragName) : null;

  const displayBenchF = useMemo(() => {
    const q = benchSearch.trim().toLowerCase();
    let list = board.benchForwards;
    if (q) list = list.filter((n) => String(n).toLowerCase().includes(q));
    return sortNamesForDisplay(list);
  }, [board.benchForwards, benchSearch]);

  const displayBenchD = useMemo(() => {
    const q = benchSearch.trim().toLowerCase();
    let list = board.benchDefense;
    if (q) list = list.filter((n) => String(n).toLowerCase().includes(q));
    return sortNamesForDisplay(list);
  }, [board.benchDefense, benchSearch]);

  const displayGoalies = useMemo(() => sortNamesForDisplay(board.goalies), [board.goalies]);

  const teamLabel = useMemo(
    () => TEAM_STATS_TEAMS.find((t) => t.teamId === teamId)?.label || teamId || 'Team',
    [teamId]
  );

  const printPracticeLinesOnly = useCallback(() => {
    const el = printSheetRef.current;
    if (!el) return;
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', 'Practice lines');
    Object.assign(iframe.style, {
      position: 'fixed',
      right: '0',
      bottom: '0',
      width: '0',
      height: '0',
      border: '0',
      opacity: '0',
      pointerEvents: 'none',
    });
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!doc || !win) {
      iframe.remove();
      return;
    }
    const pageTitle = `Practice lines — ${teamLabel}`;
    doc.open();
    doc.write(
      `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"/><title>${escapeHtmlText(pageTitle)}</title><style>${PRACTICE_LINES_IFRAME_PRINT_CSS}</style></head><body>${el.outerHTML}</body></html>`
    );
    doc.close();
    const removeIframe = () => {
      iframe.remove();
    };
    win.addEventListener('afterprint', removeIframe, { once: true });
    requestAnimationFrame(() => {
      setTimeout(() => {
        win.focus();
        win.print();
      }, 100);
    });
    setTimeout(removeIframe, 120000);
  }, [teamLabel]);

  return (
    <div className="plb-root">
      <div className="plb-toolbar plb-no-print">
        <div className="plb-toolbar-row">
          <label className="plb-team-label">
            Team
            <select className="plb-select" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
              {TEAM_STATS_TEAMS.map((t) => (
                <option key={t.teamId} value={t.teamId}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          {saveStatus ? <span className="plb-save-status">{saveStatus}</span> : null}
        </div>
        <div className="plb-intro-wrap plb-no-print">
          <p className="plb-intro-one">
            Drag skaters onto lines or forward/defense benches (default split from Stat Tracker positions). Goalies have their own pool. Layout saves per team.
          </p>
          <details className="plb-intro-details">
            <summary>How it works</summary>
            <p className="plb-intro-body">
              Data loads from the Stat Tracker team roster (<code>teamRoster</code>) and writes practice lines to Firestore for the selected team. Line colors
              match practice pinnies. Use Import/Export JSON to share a layout.
            </p>
          </details>
        </div>
        <div className="plb-tools-bar plb-no-print">
          <button
            type="button"
            className="plb-tools-toggle"
            aria-expanded={lineToolsExpanded}
            onClick={() => setLineToolsExpanded((v) => !v)}
          >
            <span className="plb-tools-toggle-chevron" aria-hidden>
              {lineToolsExpanded ? '▼' : '▶'}
            </span>
            {lineToolsExpanded ? 'Hide line tools' : 'Show line tools'}
          </button>
          <button
            type="button"
            className="admin-button plb-print-lines-btn"
            onClick={printPracticeLinesOnly}
            title="Opens a clean sheet in the print dialog — forwards bench, defense bench, goalies, and lines only. Use Save as PDF."
          >
            Print lines
          </button>
        </div>
        {lineToolsExpanded ? (
          <div className="plb-actions plb-no-print">
            <button type="button" className="admin-button" onClick={addLine}>
              Add line
            </button>
            <button type="button" className="admin-button" onClick={fullResetFromRoster}>
              Rebuild from roster
            </button>
            <button type="button" className="admin-button" onClick={resetBoard}>
              Reload saved + reconcile
            </button>
            <button type="button" className="admin-button" onClick={exportJson}>
              Export JSON
            </button>
            <button type="button" className="admin-button" onClick={() => fileInputRef.current?.click()}>
              Import JSON
            </button>
            <input ref={fileInputRef} type="file" accept="application/json,.json" className="plb-file-input" onChange={onImportFile} />
          </div>
        ) : null}
        {importError ? <p className="plb-import-error plb-no-print">{importError}</p> : null}
      </div>

      {!rosterLoaded ? (
        <p className="plb-loading">Loading roster…</p>
      ) : roster.length === 0 ? (
        <p className="plb-warning">
          No roster found for this team in Firestore. Open the Stat Tracker for this team and use &quot;Update Team Roster&quot; to save the roster first.
        </p>
      ) : null}

      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div ref={printSheetRef} className="plb-print-sheet" aria-hidden="true">
          <header className="plb-print-sheet-head">
            <h1 className="plb-print-sheet-title">Practice lines</h1>
            <p className="plb-print-sheet-meta">
              {teamLabel}
              <span className="plb-print-sheet-sep"> · </span>
              {new Date().toLocaleDateString(undefined, {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </header>
          <div className="plb-print-lines">
            {board.lines.map((line) => (
              <section key={`print-${line.id}`} className={`plb-print-line plb-print-line--${line.color}`}>
                <h2 className="plb-print-line-heading">
                  {line.label}
                  <span className="plb-print-line-pinney"> ({lineColorLabel(line.color)})</span>
                </h2>
                <table className="plb-print-table">
                  <tbody>
                    <tr>
                      <th scope="row">Forwards</th>
                      <td>
                        <span className="plb-print-cell">
                          <abbr title="Left wing">LW</abbr> {formatPlayerSlot(line.forwards[0], rosterByName)}
                        </span>
                        <span className="plb-print-cell">
                          <abbr title="Center">C</abbr> {formatPlayerSlot(line.forwards[1], rosterByName)}
                        </span>
                        <span className="plb-print-cell">
                          <abbr title="Right wing">RW</abbr> {formatPlayerSlot(line.forwards[2], rosterByName)}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th scope="row">Defense</th>
                      <td>
                        <span className="plb-print-cell">
                          <abbr title="Left defense">LD</abbr> {formatPlayerSlot(line.defense[0], rosterByName)}
                        </span>
                        <span className="plb-print-cell">
                          <abbr title="Right defense">RD</abbr> {formatPlayerSlot(line.defense[1], rosterByName)}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </section>
            ))}
          </div>
          <section className="plb-print-footer">
            <h3 className="plb-print-footer-title">Forwards bench</h3>
            <p className="plb-print-footer-body">{sortNamesForDisplay(board.benchForwards).join(' · ') || '—'}</p>
            <h3 className="plb-print-footer-title">Defense bench</h3>
            <p className="plb-print-footer-body">{sortNamesForDisplay(board.benchDefense).join(' · ') || '—'}</p>
            <h3 className="plb-print-footer-title">Goalies</h3>
            <p className="plb-print-footer-body">{sortNamesForDisplay(board.goalies).join(' · ') || '—'}</p>
          </section>
        </div>
        <div className="plb-screen-board">
          <button
            type="button"
            className={`plb-roster-backdrop plb-no-print${rosterDrawerOpen ? ' plb-roster-backdrop--visible' : ''}`}
            aria-label="Close roster panel"
            onClick={() => setRosterDrawerOpen(false)}
          />
          <div className="plb-layout">
          <div className="plb-column plb-column--pools">
            <div className={`plb-roster-shell${rosterDrawerOpen ? ' plb-roster-shell--open' : ''}`}>
              <button
                type="button"
                className="plb-roster-dock plb-no-print"
                aria-expanded={rosterDrawerOpen}
                onClick={() => setRosterDrawerOpen((o) => !o)}
              >
                <span>
                  Roster
                  <span className="plb-roster-dock-counts">
                    {' '}
                    · {board.benchForwards.length} F · {board.benchDefense.length} D · {board.goalies.length} G
                  </span>
                </span>
                <span className="plb-roster-dock-chevron" aria-hidden>
                  ▼
                </span>
              </button>
              <div className="plb-roster-panel">
                <div className="plb-pools-scroll">
                  <input
                    type="search"
                    className="plb-bench-search plb-no-print"
                    placeholder="Search forward & defense benches…"
                    value={benchSearch}
                    onChange={(e) => setBenchSearch(e.target.value)}
                    aria-label="Filter skaters on both benches by name"
                  />
                  <SkaterBenchPool
                    droppableId={DROPPABLE_BENCH_F}
                    displayNames={displayBenchF}
                    rosterByName={rosterByName}
                    sectionLabel="Skaters"
                    title="Forwards bench"
                    hint="Roster F / C / LW / RW start here. Drag to D bench or line slots anytime."
                    emptyHint="No forwards on the bench (or no search matches)."
                  />
                  <SkaterBenchPool
                    droppableId={DROPPABLE_BENCH_D}
                    displayNames={displayBenchD}
                    rosterByName={rosterByName}
                    sectionLabel="Skaters"
                    title="Defense bench"
                    hint="Roster D / LD / RD start here. Drag to forwards bench or line slots anytime."
                    emptyHint="No defense on the bench (or no search matches)."
                  />
                  <GoaliePool displayNames={displayGoalies} rosterByName={rosterByName} />
                </div>
              </div>
            </div>
          </div>
          <div className="plb-column plb-column--lines">
            <h3 className="plb-lines-heading">Lines</h3>
            <div className="plb-lines">
              {board.lines.map((line) => (
                <div
                  key={line.id}
                  className={`plb-line plb-line--${line.color}`}
                  style={{ '--plb-line-accent': `var(--plb-accent-${line.color}, #888)` }}
                >
                  <div className="plb-line-header plb-no-print">
                    <div className="plb-line-header-main">
                      <input
                        type="text"
                        className="plb-line-label-input"
                        value={line.label}
                        onChange={(e) => {
                          const v = e.target.value;
                          setBoard((prev) => ({
                            ...prev,
                            lines: prev.lines.map((l) => (l.id === line.id ? { ...l, label: v } : l)),
                          }));
                        }}
                        aria-label="Line label"
                      />
                      <select
                        className="plb-select plb-line-color-select"
                        value={line.color}
                        onChange={(e) => {
                          const v = e.target.value;
                          setBoard((prev) => ({
                            ...prev,
                            lines: prev.lines.map((l) => (l.id === line.id ? { ...l, color: v } : l)),
                          }));
                        }}
                        aria-label="Line color"
                      >
                        {LINE_COLOR_OPTIONS.map((c) => (
                          <option key={c.key} value={c.key}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <details className="plb-line-actions">
                      <summary className="plb-line-actions-summary">Actions</summary>
                      <div className="plb-line-actions-menu">
                        <button type="button" className="admin-button" onClick={() => duplicateLine(line.id)}>
                          Duplicate line
                        </button>
                        <button type="button" className="admin-button" onClick={() => clearLine(line.id)}>
                          Clear line
                        </button>
                        <button type="button" className="admin-button" onClick={() => removeLine(line.id)} disabled={board.lines.length <= 1}>
                          Remove line
                        </button>
                      </div>
                    </details>
                  </div>
                  <div className="plb-line-grid">
                    <div className="plb-line-row">
                      <span className="plb-pos-label">LW</span>
                      <SlotCell lineId={line.id} kind="f" index={0} occupantName={line.forwards[0]} rosterByName={rosterByName} />
                      <span className="plb-pos-label">C</span>
                      <SlotCell lineId={line.id} kind="f" index={1} occupantName={line.forwards[1]} rosterByName={rosterByName} />
                      <span className="plb-pos-label">RW</span>
                      <SlotCell lineId={line.id} kind="f" index={2} occupantName={line.forwards[2]} rosterByName={rosterByName} />
                    </div>
                    <div className="plb-line-row">
                      <span className="plb-pos-label">LD</span>
                      <SlotCell lineId={line.id} kind="d" index={0} occupantName={line.defense[0]} rosterByName={rosterByName} />
                      <span className="plb-pos-label">RD</span>
                      <SlotCell lineId={line.id} kind="d" index={1} occupantName={line.defense[1]} rosterByName={rosterByName} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DragOverlay>
          {activeDragName ? (
            <div className="plb-player-card plb-player-card--overlay">
              <span className="plb-player-num">{activePlayer?.number != null ? `#${activePlayer.number}` : ''}</span>
              <span className="plb-player-name">{activeDragName}</span>
            </div>
          ) : null}
        </DragOverlay>
        </div>
      </DndContext>
    </div>
  );
}
